<template>
  <div v-if="isModalOpen" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h2>Agregar Plan Individual</h2>
        <button class="close-btn" @click="closeModal">×</button>
      </div>
      <div class="modal-body">
        <p>Selecciona las competencias a mejorar y agrega una propuesta de solución.</p>
        <div v-if="loading" class="loading">Cargando competencias...</div>
        <div v-else class="modal-layout">
          <div v-if="averageEvaluations.length" class="competency-list">
            <h3>Promedio de Competencias</h3>
            <div class="d-flex justify-content-between">
              <span> Competencia </span>
              <span> Promedio </span>
            </div>
             <div class="d-flex justify-content-between" v-for="competency in averageEvaluations" :key="competency.competency_name">
              <span>{{ competency.competency_name }}</span>
              <span class="font-bold">{{ competency.average_score.toFixed(1) }} Pts</span>
            </div>
          </div>
          <div class="form-container mt-3">
            <div class="competency-select d-flex justify-content-between">
              <select v-model="selectedCompetency" class="select-input">
                <option disabled value="">Selecciona una competencia</option>
                <option v-for="competency in availableCompetencies" :key="competency.id" :value="competency">
                  {{ competency.name }}
                </option>
              </select>
              <button class="btn-secondary mt-2 ml-2" :disabled="!selectedCompetency" @click="addCompetency">Agregar</button>
            </div>
            <form @submit.prevent="submitPlan">
              <div v-for="(competency, index) in addedCompetencies" :key="competency.id" class="competency-item">
                <p><b>{{ competency.name }}</b></p>
                <textarea
                  v-model="solutions[index]"
                  :class="{ 'invalid-input': !isSolutionValid(index) && submitted }"
                  placeholder="Escribe una propuesta de solución"
                  class="select-input"
                ></textarea>
                <p v-if="!isSolutionValid(index) && submitted" class="error-message">
                  La solución debe tener al menos 50 caracteres.
                </p>
                <button type="button" class="remove-btn" @click="removeCompetency(index)">Eliminar</button>
              </div>
              <p v-if="addedCompetencies.length < 2 && submitted" class="error-message">
                Debes agregar al menos 2 competencias.
              </p>
              <button type="submit" class="btn-success mt-2" :disabled="saving">{{ saving ? 'Guardando...' : 'Guardar Plan' }}</button>
            </form>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="close-modal-btn" @click="closeModal">Cerrar</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import request from "../../services/request";

export default {
  name: "IndividualPlanModal",
  props: {
    isModalOpen: Boolean,
    evaluationId: Number,
    userId: Number,
  },
  data() {
    return {
      competencies: [],
      availableCompetencies: [],
      averageEvaluations: [],
      addedCompetencies: [],
      solutions: [],
      selectedCompetency: null,
      loading: false,
      saving: false,
      submitted: false,
    };
  },
  watch: {
    isModalOpen(newVal) {
      if (newVal) {
        this.fetchCompetencies(this.evaluationId, this.userId);
      }
    },
    evaluationId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchCompetencies(newVal, this.userId);
        }
      },
    },
  },
  computed: {
    isFormValid() {
      return (
        this.addedCompetencies.length >= 2 &&
        this.solutions.every((solution) => solution && solution.trim().length >= 50)
      );
    },
  },
  methods: {
    async fetchCompetencies(evaluationId, userId) {
      this.loading = true;
      try {
        const { data } = await request.get(`/evaluations/${evaluationId}/${userId}/competencies`);
        this.competencies = data.competencies;
        this.availableCompetencies = [...this.competencies];
        this.averageEvaluations = data.last_evaluations || [];
      } catch (error) {
        console.error("Error al obtener las competencias:", error);
      } finally {
        this.loading = false;
      }
    },
    addCompetency() {
      if (this.selectedCompetency) {
        this.addedCompetencies.push(this.selectedCompetency);
        this.solutions.push("");
        this.availableCompetencies = this.availableCompetencies.filter(
          (competency) => competency.id !== this.selectedCompetency.id
        );
        this.selectedCompetency = null;
      }
    },
    removeCompetency(index) {
      const removed = this.addedCompetencies.splice(index, 1)[0];
      this.solutions.splice(index, 1);
      this.availableCompetencies.push(removed);
    },
    isSolutionValid(index) {
      return this.solutions[index] && this.solutions[index].trim().length >= 50;
    },
    async submitPlan() {
      this.submitted = true;
      if (!this.isFormValid || this.saving) return;
      
      this.saving = true;
      try {
        const payload = this.addedCompetencies.map((competency, index) => ({
          competency_id: competency.id,
          solution: this.solutions[index],
        }));
        await request.post(`/evaluations/individual-plan`, {
          plan: payload,
          evaluation_id: this.evaluationId,
          user_id: this.userId,
        });
        this.successNotification("Plan individual guardado exitosamente.");

        this.$emit("plan-saved");
        this.closeModal();
      } catch (error) {
        this.errorNotification("Error al guardar el plan individual. Por favor, inténtelo de nuevo.");
      } finally {
        this.saving = false;
      }
    },
    closeModal() {
      this.submitted = false;
      this.addedCompetencies = [];
      this.solutions = [];
      this.selectedCompetency = null;
      this.saving = false;
      this.$emit("close");
    },
     ...mapActions({
      successNotification: 'alert/success',
      errorNotification: 'alert/error',
    }),
  },
};
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 14px;
  margin-top: 5px;
}
.invalid-input {
  border: 1px solid red;
}
.select-input,
.solution-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
}
button {
  background-color: #1d4745;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
}
button:hover {
  background-color: #163a39;
}
.remove-btn {
  background-color: #c0392b;
}
.remove-btn:hover {
  background-color: #a93226;
}
.submit-btn {
  margin-top: 10px;
  background-color: #27ae60;
}
.submit-btn:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
}
.modal-content {
  max-width: 600px;
  max-height: 80vh; /* Limita la altura del modal */
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-body {
  flex: 1; /* Permite que crezca dentro del modal */
  overflow-y: auto; /* Habilita el scroll cuando el contenido es muy largo */
  max-height: 60vh; /* Evita que el modal crezca demasiado */
  padding: 10px;
}

/* Para asegurar que el modal sea visible en pantallas pequeñas */
@media (max-width: 768px) {
  .modal-content {
    max-width: 90%;
    max-height: 95vh;
  }
  .modal-body {
    max-height: 90vh;
  }
}

</style>